import { css } from '@emotion/react';
import { useContext, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import Link from '../../components/Link';
import PageContainer from '../../components/PageContainer';
import Button from '../../components/Button';
import VerifyCodeForm from './VerifyCodeForm';
import useMessageService from '../../services/i18n/useMessageService';
import useUserProfile from '../../services/useUserProfile';
import useNetworkErrorNotification from '../../services/useNetworkErrorNotification';
import LoadingScreenContext from '../../services/LoadingScreenContext';
import { resendVerificationCode } from '../../veevaIdApi';
import ResponseStatus from '../../constants/ResponseStatus';
import {
    getErrorMessage,
    getFirstError,
    showErrorNotificationFromResponse,
} from '../../services/errorUtils';
import UnauthenticatedRouteLanguageContext from '../../services/UnauthenticatedRouteLanguageContext';
import ResponseErrorTypes from '../../constants/ResponseErrorTypes';
import getTargetAttribute from '../../services/getTargetAttribute';
import LocalizedMessage from '../../components/LocalizedMessage';

const RESEND_CODE_TIMEOUT_MS_DURATION = 5000;

const VerifyCodePage = ({
    onSubmit,
    onSubmitFail,
    onSubmitFinish,
    onSubmitStart,
    onSubmitSuccess,
}) => {
    const [showResendClicked, setShowResendClicked] = useState(false);
    const [errorResponse, setErrorResponse] = useState();
    const [errorMessage, setErrorMessage] = useState();
    const [userProfile] = useUserProfile();
    const { showNotification } = useNetworkErrorNotification();
    const { setLoading } = useContext(LoadingScreenContext);
    const { messageLanguage } = useContext(UnauthenticatedRouteLanguageContext);
    const [
        [
            title = 'Enter Your Verification Code',
            description = 'We sent your code to {0}',
            buttonLabel = 'Verify',
            supportLinkLabel = 'Contact support',
            accountLockedError = 'Your account is locked due to too many authentication attempts.',
        ],
    ] = useMessageService([
        'verify_code.enter_your_code',
        'verify_code.sent_code_to_email',
        'verify_code.verify',
        'general.contact_support',
        'error.veevaid_account_locked_disabled',
    ]);
    const [value, setValue] = useState('');
    const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);
    const [resendLinkDisabled, setResendLinkDisabled] = useState(false);
    const [inputsDisabled, setInputsDisabled] = useState(false);
    const [shouldFocusInputAfterEnable, setShouldFocusInputAfterEnable] = useState(false);
    const resetFormCallback = useRef();
    const focusInputCallback = useRef();

    // Allow inputs to be re-focused after enabling them again
    useEffect(() => {
        if (!inputsDisabled && shouldFocusInputAfterEnable) {
            focusInputCallback.current?.(0);
            setShouldFocusInputAfterEnable(false);
        }
    }, [shouldFocusInputAfterEnable, inputsDisabled]);

    useEffect(() => {
        const setVerifyCodePageErrorMessage = async () => {
            if (errorResponse?.responseStatus !== ResponseStatus.FAILURE) {
                setErrorMessage('');
                return;
            }

            const error = getFirstError(errorResponse);
            switch (error.type) {
                case ResponseErrorTypes.VEEVAID_ACCOUNT_LOCKED_DISABLED: {
                    setErrorMessage(
                        <LocalizedMessage
                            template={`${accountLockedError} {0}`}
                            tokens={[
                                <Link
                                    key="support-link"
                                    href="https://sites.veevavault.help/gr/resources/support/"
                                    css={css`
                                        font-size: 12px;
                                    `}
                                >
                                    {supportLinkLabel}
                                </Link>,
                            ]}
                        />,
                    );
                    break;
                }
                case ResponseErrorTypes.VEEVAID_RESEND_VERIFICATION_CODE:
                case ResponseErrorTypes.VEEVAID_VERIFICATION_CODE_INVALID:
                case ResponseErrorTypes.VEEVAID_CODE_RESEND_LIMIT: {
                    const errMsg = await getErrorMessage(error, messageLanguage);
                    setErrorMessage(errMsg);
                    break;
                }
                default: {
                    const i18nOptions = { language: messageLanguage };
                    await showErrorNotificationFromResponse(errorResponse, i18nOptions);
                }
            }
        };
        setVerifyCodePageErrorMessage();
    }, [accountLockedError, errorResponse, messageLanguage, supportLinkLabel]);

    const setResetFormRef = (callback) => {
        resetFormCallback.current = callback;
    };

    const setFocusInputRef = (callback) => {
        focusInputCallback.current = callback;
    };

    const handleChange = (newValue) => {
        setValue(newValue);
        setErrorResponse(undefined);
        setSubmitButtonDisabled(false);
    };

    const handleSubmitFailure = async (response) => {
        const responseError = getFirstError(response);
        setErrorResponse(response);

        switch (responseError.type) {
            case ResponseErrorTypes.VEEVAID_VERIFICATION_CODE_INVALID:
                setSubmitButtonDisabled(true);
                focusInputCallback.current?.();
                break;
            case ResponseErrorTypes.VEEVAID_RESEND_VERIFICATION_CODE:
                setSubmitButtonDisabled(true);
                setInputsDisabled(true);
                break;
            case ResponseErrorTypes.VEEVAID_ACCOUNT_LOCKED_DISABLED:
                setSubmitButtonDisabled(true);
                setResendLinkDisabled(true);
                setInputsDisabled(true);
                break;
            default:
            // no-op
        }
    };

    const handleSubmit = debounce(async () => {
        onSubmitStart?.();
        const response = await onSubmit?.(value);
        onSubmitFinish?.();
        switch (response.responseStatus) {
            case ResponseStatus.SUCCESS:
                onSubmitSuccess?.(response);
                break;
            case ResponseStatus.FAILURE: {
                onSubmitFail?.();
                if (response?.data?.targetUrl) {
                    window.location.href = response.data.targetUrl;
                } else {
                    await handleSubmitFailure(response);
                }
                break;
            }
            case ResponseStatus.UI_NETWORK_ERROR: {
                onSubmitFail?.();
                await showNotification();
                setSubmitButtonDisabled(true);
                focusInputCallback.current?.();
                break;
            }
            default:
            // no-op
        }
    }, 250);

    const handleCodeResend = async () => {
        setLoading(true);
        const response = await resendVerificationCode();
        setLoading(false);
        setSubmitButtonDisabled(false);
        setInputsDisabled(false);
        setErrorResponse(undefined);
        switch (response.responseStatus) {
            case ResponseStatus.SUCCESS:
                setShowResendClicked(true);
                resetFormCallback.current?.();
                setShouldFocusInputAfterEnable(true);
                setTimeout(() => {
                    setShowResendClicked(false);
                }, RESEND_CODE_TIMEOUT_MS_DURATION);
                break;
            case ResponseStatus.FAILURE: {
                setErrorResponse(response);
                break;
            }
            case ResponseStatus.UI_NETWORK_ERROR: {
                await showNotification();
                break;
            }
            default:
            // no-op
        }
    };

    return (
        <PageContainer title={title}>
            <div
                css={css`
                    margin-bottom: 2rem;
                    text-align: center;
                    font-size: 16px;
                    font-weight: bold;
                `}
            >
                {description?.replace('{0}', userProfile?.username)}
            </div>
            <VerifyCodeForm
                onChange={handleChange}
                error={errorMessage}
                focusInputRef={setFocusInputRef}
                showResendClicked={showResendClicked}
                onCodeResend={handleCodeResend}
                onSubmit={handleSubmit}
                resetRef={setResetFormRef}
                resendDisabled={resendLinkDisabled}
                inputsDisabled={inputsDisabled}
            />
            <Button
                onClick={handleSubmit}
                disabled={value?.length !== 4 || submitButtonDisabled}
                data-target={getTargetAttribute('submit-button')}
            >
                {buttonLabel}
            </Button>
        </PageContainer>
    );
};

VerifyCodePage.propTypes = {
    /**
     * Function called that should be an endpoint wrapper
     * for the action that occurs on code submission.
     * Passes in the new code as the first parameter,
     * and must return the result of the endpoint.
     * const response = await onSubmit(code)
     */
    onSubmit: PropTypes.func,

    /**
     * Callback triggered when onSubmit has failed
     */
    onSubmitFail: PropTypes.func,

    /**
     * Callback triggered when onSubmit has finished
     */
    onSubmitFinish: PropTypes.func,

    /**
     * Callback triggered when onSubmit has started
     */
    onSubmitStart: PropTypes.func,

    /**
     * Callback triggered when onSubmit has succeeded.
     * Called with the response of onSubmit as the first parameter.
     * onSubmitSuccess(response)
     */
    onSubmitSuccess: PropTypes.func,
};

export default VerifyCodePage;
